import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "首頁", page: "/", hash: "" },
  { label: "關於我們", page: "/about", hash: "" },
  { label: "能量系列", page: "/products", hash: "" },
  { label: "知識專欄", page: "/knowledge", hash: "" },
  { label: "人氣精選", page: "/", hash: "#featured" },
  { label: "顧客故事", page: "/", hash: "#testimonials" },
  { label: "聯絡我們", page: "/contact", hash: "" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    // Reset on route change
    setScrolled(window.scrollY > 60);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // On sub-pages, always show solid header
  const isSubPage = location.pathname !== "/";
  const showSolid = scrolled || isSubPage;

  const handleNavClick = (page: string, hash: string) => {
    setMenuOpen(false);
    if (page === "/" && hash) {
      if (location.pathname === "/") {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 400);
      }
    } else {
      navigate(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        showSolid
          ? "bg-[#faf8f4] shadow-sm"
          : "bg-[#2a1f18]/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <button
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/96dfeacc-3b61-4651-b117-af35c256fe09.png"
            alt="瓏靖手作能量 Logo"
            className="h-12 w-auto object-contain"
          />
          <span
            className={`font-semibold tracking-widest text-[1.0625rem] transition-colors duration-500 hidden sm:block ${
              showSolid ? "text-[#3d3530]" : "text-[#f5ede3]"
            }`}
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            瓏靖手作能量
          </span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive =
              link.page !== "/" && location.pathname === link.page;
            return (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.page, link.hash)}
                className={`text-[1.0625rem] font-medium tracking-wide transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "text-[#8b6f47]"
                    : showSolid
                    ? "text-[#5a4e45] hover:text-[#8b6f47]"
                    : "text-[#f5ede3] hover:text-white"
                }`}
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {link.label}
              </button>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => handleNavClick("/products", "")}
            className={`px-5 py-2.5 rounded-full text-[1rem] font-medium tracking-wide whitespace-nowrap cursor-pointer transition-all duration-300 ${
              showSolid
                ? "bg-[#8b6f47] text-white hover:bg-[#7a5f3a]"
                : "bg-[#8b6f47]/90 text-white border border-[#c9a97a]/60 hover:bg-[#8b6f47]"
            }`}
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            探索能量系列
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg bg-[#8b6f47] text-white transition-colors duration-300 hover:bg-[#7a5f3a]"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="開啟選單"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          menuOpen ? "max-h-[560px] opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ background: "#faf8f4", borderTop: "1px solid #e8e0d5", boxShadow: "0 8px 24px rgba(61,53,48,0.18)" }}
      >
        <nav className="flex flex-col px-6 py-5 gap-1">
          {navLinks.map((link) => {
            const isActive = link.page !== "/" && location.pathname === link.page;
            return (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.page, link.hash)}
                className={`text-left text-[1.0625rem] font-medium py-3.5 border-b border-[#e8e0d5] last:border-0 cursor-pointer transition-colors whitespace-nowrap ${
                  isActive ? "text-[#8b6f47]" : "text-[#3d3530] hover:text-[#8b6f47]"
                }`}
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {link.label}
              </button>
            );
          })}
          <button
            onClick={() => handleNavClick("/products", "")}
            className="mt-4 w-full py-3.5 bg-[#8b6f47] text-white rounded-full text-[1rem] font-medium cursor-pointer hover:bg-[#7a5f3a] transition-colors whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            探索能量系列
          </button>
        </nav>
      </div>
    </header>
  );
}
